// Connecting a business to the system its appointments and leads actually live in.
//
// Until this exists, book_appointment cannot work for anyone: the tools have to
// reach the CUSTOMER's system, and there was nowhere to say what that is.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plug, Plus, RefreshCw, Trash2, CheckCircle2, XCircle, Loader2, AlertCircle, X, Search,
  ToggleLeft, ToggleRight, AlertTriangle, ListChecks,
} from 'lucide-react';
import { SHAPES, ORDER, matchesQuery } from './crmProviderShapes';
import {
  crmProvidersService, CrmProvider, CrmProviderType,
} from 'api/services/crmProviders.service';
import CustomApiForm from './CustomApiForm';
import { formFromConfig, splitServerProblems, CUSTOM_API_DELIVERY_ROLES } from './customApiShapes';
import { useAuth } from 'hooks/useAuth';


const BLANK: CrmProvider = {
  providerType: 'CATCHMYCALL', authType: 'NONE',
  supportsBooking: true, supportsLeads: false, enabled: true,
};

const CrmIntegrations: React.FC = () => {
  const [rows, setRows] = useState<CrmProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CrmProvider | null>(null);
  const [typeQuery, setTypeQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Inside the form, not the page: the page banner sits BEHIND the modal, so a
  // refused save used to look like a Save button that did nothing.
  const [formErrors, setFormErrors] = useState<string[]>([]);
  // What the Custom API form says the server would refuse; Save waits for none.
  const [customProblems, setCustomProblems] = useState<string[]>([]);
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role || '').toLowerCase();
  const canSeeDeliveries = CUSTOM_API_DELIVERY_ROLES.includes(role);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const res = await crmProvidersService.list();
    if (res.success) setRows(res.data || []);
    else setError(res.error || 'Failed to load integrations');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const shape = (t: CrmProviderType) => SHAPES[t] || SHAPES.N8N;

  const pickType = (t: CrmProviderType) => {
    if (!editing) return;
    const s = shape(t);
    // A Custom API config and a vendor's settings share the configJson column
    // and nothing else, so crossing between them starts that field afresh
    // rather than carry e.g. Zenoti's {"center_id"} into a request description.
    const crossing = !!s.customApi !== !!shape(editing.providerType).customApi;
    setCustomProblems([]);
    setEditing({
      ...editing,
      providerType: t,
      authType: s.auth,
      supportsBooking: s.surfaces.booking,
      supportsLeads: s.surfaces.leads,
      accountsServer: editing.accountsServer || s.needs.accountsServer,
      apiDomain: editing.apiDomain || s.needs.apiDomain,
      configJson: crossing ? (s.customApi ? '' : s.needs.config) : (editing.configJson || s.needs.config),
    });
  };

  const openEditor = (row: CrmProvider) => {
    setFormErrors([]);
    setCustomProblems([]);
    setEditing({ ...row });
  };

  /**
   * Bad JSON here is the quiet kind of wrong: the backend logs it and carries on
   * with no mapping, so the form would look saved and every custom field would
   * silently stop being sent. So it is checked before the save, not after.
   */
  const MAPPING_EXAMPLE = `{\n  "lead_defaults": { "Lead_Status": "Not Contacted" },\n  "lead_field_map": {\n    "first_name": "FirstName",\n    "company": "Company",\n    "wellness_goal": "PrimaryWellness_Goal"\n  },\n  "lead_picklists": {\n    "PrimaryWellness_Goal": ["Detox", "Weight Loss"]\n  },\n  "lead_notes_field": "Remarks"\n}`;

  const mapping = (() => {
    // The Custom API form owns its configJson and checks it itself.
    if (editing && shape(editing.providerType).customApi) return { error: null as string | null, summary: null as string | null };
    const raw = (editing?.configJson || '').trim();
    if (!raw) return { error: null as string | null, summary: null as string | null };
    try {
      const o = JSON.parse(raw);
      if (typeof o !== 'object' || Array.isArray(o) || o === null) {
        return { error: 'Field mapping must be a JSON object', summary: null };
      }
      const fields = Object.keys(o.lead_field_map || {}).length;
      const lists = Object.keys(o.lead_picklists || {}).length;
      const defs = Object.keys(o.lead_defaults || {}).length;
      return {
        error: null,
        summary: `${fields} field${fields === 1 ? '' : 's'} mapped · ${lists} picklist${lists === 1 ? '' : 's'} · ${defs} default${defs === 1 ? '' : 's'}`,
      };
    } catch (e: any) {
      return { error: `Not valid JSON: ${e.message}`, summary: null };
    }
  })();
  const mappingError = mapping.error;
  const mappingSummary = mapping.summary;

  const save = async () => {
    if (!editing) return;
    if (mappingError) { setFormErrors([mappingError]); return; }
    if (shape(editing.providerType).customApi && customProblems.length) {
      setFormErrors(['Fix the problems listed above first — the server would refuse this configuration.']);
      return;
    }
    setSaving(true); setError(null); setFormErrors([]);
    const res = await crmProvidersService.save(editing);
    setSaving(false);
    // CustomApiConfig.parse answers 400 with EVERY problem in one sentence;
    // one per line is what makes them fixable.
    if (!res.success) { setFormErrors(splitServerProblems(res.error || 'Save failed')); return; }
    setEditing(null);
    load();
  };

  /**
   * Turn an integration on or off without opening the form.
   *
   * The whole row is sent back because the API takes the object, not a patch;
   * secrets are never returned by the server, so they are simply absent and the
   * backend keeps what it holds.
   */
  const toggleEnabled = async (row: CrmProvider) => {
    setBusyId(row.id || null);
    const res = await crmProvidersService.save({ ...row, enabled: !row.enabled });
    if (!res.success) setError(res.error || 'Could not change that');
    setBusyId(null);
    await load();
  };

  /**
   * More than one integration enabled for the same job.
   *
   * The backend resolves a provider with findFirst(...OrderByCreatedAtAsc), so
   * the OLDEST enabled one wins and the rest receive nothing at all. They still
   * show as connected, which is the worst kind of wrong — so it is said plainly
   * here rather than left to be discovered when leads go missing.
   */
  const conflicts = (['leads', 'booking'] as const).map(kind => {
    const live = rows
      .filter(r => r.enabled && (kind === 'leads' ? r.supportsLeads : r.supportsBooking))
      .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
    return {
      kind,
      names: live.map(r => r.name || shape(r.providerType).label),
      winner: live.length ? (live[0].name || shape(live[0].providerType).label) : '',
    };
  }).filter(c => c.names.length > 1);

  const test = async (row: CrmProvider) => {
    if (!row.id) return;
    setTesting(row.id);
    const res = await crmProvidersService.test(row.id);
    setTesting(null);
    // The endpoint answers 200 with ok:false and a reason; the reason is the
    // point, so it is shown either way rather than collapsed into "failed".
    if (!res.success) setError(res.error || 'Test failed');
    load();
  };

  const remove = async (row: CrmProvider) => {
    if (!row.id) return;
    const res = await crmProvidersService.remove(row.id);
    if (!res.success) { setError(res.error || 'Could not remove'); return; }
    load();
  };

  const input = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm';
  const lbl = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <Plug className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            Booking &amp; CRM integrations
          </h1>
          <p className="text-[13px] mt-1 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
            Where this business's appointments and leads live. The AI agent books into whichever
            system is connected here — you never configure the agent itself.
          </p>
        </div>
        <div className="flex gap-2">
          {canSeeDeliveries && rows.some(r => r.providerType === 'CUSTOM_API') && (
            <Link className="tl-btn tl-btn-secondary tl-btn-sm" to="/crm/custom-api-deliveries">
              <ListChecks className="w-3.5 h-3.5" /> Custom API deliveries
            </Link>
          )}
          <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={load}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button className="tl-btn tl-btn-primary" onClick={() => openEditor({ ...BLANK })}>
            <Plus className="w-4 h-4" /> Connect a system
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          {/* Without this the banner is a dead end. load() runs on mount only,
              so a momentary failure — a backend restart, a dropped connection —
              leaves a red box sitting there permanently, describing something
              that stopped being true seconds later. That is indistinguishable
              from a real outage to the person looking at it. */}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="underline underline-offset-2 font-medium disabled:opacity-50 whitespace-nowrap"
          >
            {loading ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="tl-card text-center py-10">
          <Plug className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-medium text-gray-900 dark:text-white">Nothing connected yet</p>
          <p className="text-[13px] mt-1 max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
            Until something is connected the agent cannot take a booking — it will tell callers
            so rather than pretend. If this business has no CRM, use the built-in one.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {conflicts.map(c => (
            <div key={c.kind} className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm
                                         text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              <div className="flex gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">
                    {c.names.length} integrations are enabled for {c.kind}, but only one is ever used.
                  </span>{' '}
                  Everything goes to <strong>{c.winner}</strong> — the one added first. The
                  {c.names.length > 2 ? ' others' : ' other'} will look connected and receive nothing.
                  Disable {c.names.length > 2 ? 'the ones' : 'the one'} you are not using.
                </div>
              </div>
            </div>
          ))}
          {rows.map(r => (
            <div key={r.id} className="tl-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {r.name || shape(r.providerType).label}
                    </span>
                    <span className="tl-pill">{shape(r.providerType).label}</span>
                    {r.supportsBooking && <span className="tl-pill">booking</span>}
                    {r.supportsLeads && <span className="tl-pill">leads</span>}
                    {r.providerType === 'CUSTOM_API' && (() => {
                      const f = formFromConfig(r.configJson);
                      return (
                        <>
                          <span className="tl-pill">{f.mode === 'FETCH' ? 'in-call lookup' : 'push'}</span>
                          {f.mode === 'FETCH' && f.toolName && <span className="tl-pill font-mono">{f.toolName}</span>}
                        </>
                      );
                    })()}
                    {!r.enabled && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">disabled</span>
                    )}
                  </div>

                  {/* The last test is the only honest signal that this works. */}
                  <div className="mt-2 flex items-start gap-1.5 text-[12.5px]">
                    {r.lastTestOk === true && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />}
                    {r.lastTestOk === false && <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />}
                    <span style={{ color: 'var(--text-muted)' }}>
                      {r.lastTestMessage
                        ? r.lastTestMessage
                        : 'Never tested — run Test connection before a caller relies on it.'}
                    </span>
                  </div>
                  {r.providerType === 'CUSTOM_API' && (
                    <p className="mt-1 text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                      {formFromConfig(r.configJson).mode === 'FETCH'
                        ? <>Give an agent the tool <code>/api/v1/agent-tools/&#123;agent_id&#125;/api/{formFromConfig(r.configJson).toolName}</code>. </>
                        : <>Used only by agents bound to it — Agents → the agent → Custom API push. </>}
                      Test connection checks the configuration and sends nothing: a test push would file a real record.
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button className="tl-btn tl-btn-secondary tl-btn-sm"
                          disabled={busyId === r.id}
                          title={r.enabled ? 'Stop using this integration' : 'Start using this integration'}
                          onClick={() => toggleEnabled(r)}>
                    {busyId === r.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : r.enabled ? <ToggleRight className="w-3.5 h-3.5 text-green-600" />
                                  : <ToggleLeft className="w-3.5 h-3.5 text-gray-400" />}
                    {r.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                  <button className="tl-btn tl-btn-secondary tl-btn-sm"
                          title={r.providerType === 'CUSTOM_API'
                            ? 'Validates the configuration and renders the template. Nothing is sent.' : undefined}
                          disabled={testing === r.id} onClick={() => test(r)}>
                    {testing === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Test connection
                  </button>
                  <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => openEditor(r)}>Edit</button>
                  <button className="tl-icon-btn" onClick={() => remove(r)} title="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'var(--scrim)' }}>
          <div className={`tl-card w-full ${shape(editing.providerType).customApi ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editing.id ? 'Edit integration' : 'Connect a system'}
              </h2>
              <button className="tl-icon-btn" onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className={lbl}>System</label>
                <div className="relative mt-1">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2"
                          style={{ color: 'var(--text-muted)' }} />
                  <input className={`${input} pl-8`} value={typeQuery}
                         onChange={e => setTypeQuery(e.target.value)}
                         placeholder="Search — CatchMyCall, Zoho, Zenoti, Practo, Google Sheets…" />
                </div>
                <div className="mt-2 grid gap-1.5 max-h-64 overflow-y-auto">
                  {ORDER.filter(t => matchesQuery(t, typeQuery)).map(t => {
                    const sh = SHAPES[t];
                    const Icon = sh.icon;
                    const on = editing.providerType === t;
                    return (
                      <button key={t} type="button" onClick={() => pickType(t)}
                        className={`text-left flex gap-2.5 items-start rounded-lg border p-2.5 transition-colors
                                    ${on ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}>
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${on ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span>
                          <span className="block text-sm font-medium">{sh.label}</span>
                          <span className="block text-[11px]" style={{ color: 'var(--text-muted)' }}>{sh.blurb}</span>
                        </span>
                      </button>
                    );
                  })}
                  {ORDER.filter(t => matchesQuery(t, typeQuery)).length === 0 && (
                    <p className="text-[11px] px-1 py-3" style={{ color: 'var(--text-muted)' }}>
                      Nothing matches “{typeQuery}”. Anything with an API can go through
                      the n8n workflow option — clear the search to see it.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className={lbl}>Name <span style={{ color: 'var(--text-muted)' }}>— optional</span></label>
                <input className={input} value={editing.name || ''}
                       onChange={e => setEditing({ ...editing, name: e.target.value })}
                       placeholder="e.g. Bandra branch Zenoti" />
              </div>

              {shape(editing.providerType).customApi && (
                <CustomApiForm
                  key={`${editing.id || 'new'}`}
                  provider={editing}
                  onChange={patch => setEditing(prev => (prev ? { ...prev, ...patch } : prev))}
                  onProblems={setCustomProblems} />
              )}

              {shape(editing.providerType).needs.accountsServer && (
                <div>
                  <label className={lbl}>Zoho datacentre</label>
                  <input className={input + ' font-mono text-xs'} value={editing.accountsServer || ''}
                         onChange={e => setEditing({ ...editing, accountsServer: e.target.value })}
                         placeholder="https://accounts.zoho.in" />
                  <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    Region-specific — <code>.in</code> for India, <code>.com</code> for the US.
                    The API host is discovered automatically on the first test.
                  </p>
                </div>
              )}

              {shape(editing.providerType).needs.apiDomain && (
                <div>
                  <label className={lbl}>API host</label>
                  <input className={input + ' font-mono text-xs'} value={editing.apiDomain || ''}
                         onChange={e => setEditing({ ...editing, apiDomain: e.target.value })}
                         placeholder={shape(editing.providerType).needs.apiDomain} />
                </div>
              )}

              {shape(editing.providerType).needs.clientId && (
                <div>
                  <label className={lbl}>{shape(editing.providerType).needs.clientId}</label>
                  <input className={input + ' font-mono text-xs'} value={editing.clientId || ''}
                         onChange={e => setEditing({ ...editing, clientId: e.target.value })} />
                </div>
              )}

              {shape(editing.providerType).needs.clientSecret && (
                <div>
                  <label className={lbl}>Client secret</label>
                  <input type="password" className={input + ' font-mono text-xs'}
                         value={editing.clientSecret || ''}
                         onChange={e => setEditing({ ...editing, clientSecret: e.target.value })}
                         placeholder={editing.hasClientSecret ? '•••••• already set — leave blank to keep' : ''} />
                </div>
              )}

              {shape(editing.providerType).needs.secret && (
                <div>
                  <label className={lbl}>{shape(editing.providerType).needs.secret}</label>
                  <input type="password" className={input + ' font-mono text-xs'}
                         value={editing.refreshToken || ''}
                         onChange={e => setEditing({ ...editing, refreshToken: e.target.value })}
                         placeholder={editing.hasRefreshToken ? '•••••• already set — leave blank to keep' : ''} />
                  {/* Blank means "unchanged" server-side. Saying so stops someone
                      retyping a secret they cannot see, or fearing they wiped it. */}
                  {editing.hasRefreshToken && (
                    <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      Already stored. Leave blank unless you are replacing it.
                    </p>
                  )}
                </div>
              )}


              {shape(editing.providerType).fieldMapping && (
                <div>
                  <label className={lbl}>
                    Field mapping <span style={{ color: 'var(--text-muted)' }}>— optional</span>
                  </label>
                  <p className="text-[11px] mb-1" style={{ color: 'var(--text-muted)' }}>
                    Only needed if your CRM uses its own field names, or has picklists.
                    Leave empty and the standard name, phone, email and notes are sent.
                  </p>
                  <textarea rows={10} className={input + ' font-mono text-xs'}
                            value={editing.configJson || ''}
                            onChange={e => setEditing({ ...editing, configJson: e.target.value })}
                            placeholder={MAPPING_EXAMPLE} />
                  {mappingError ? (
                    <p className="text-[11px] mt-1 text-red-600 dark:text-red-400">
                      {mappingError} — saving this would silently switch mapping OFF.
                    </p>
                  ) : mappingSummary ? (
                    <p className="text-[11px] mt-1 text-green-700 dark:text-green-400">{mappingSummary}</p>
                  ) : null}
                  <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    A picklist value your CRM does not recognise is written into the notes field
                    instead of being sent — otherwise the CRM rejects the whole lead.
                  </p>
                </div>
              )}
              {shape(editing.providerType).needs.config && (
                <div>
                  <label className={lbl}>Settings</label>
                  <textarea rows={3} className={input + ' font-mono text-xs'}
                            value={editing.configJson || ''}
                            onChange={e => setEditing({ ...editing, configJson: e.target.value })}
                            placeholder={shape(editing.providerType).needs.config} />
                </div>
              )}

              <div className="flex gap-4 pt-1">
                {!shape(editing.providerType).customApi && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={!!editing.supportsBooking}
                             onChange={e => setEditing({ ...editing, supportsBooking: e.target.checked })} />
                      Use for booking
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={!!editing.supportsLeads}
                             onChange={e => setEditing({ ...editing, supportsLeads: e.target.checked })} />
                      Use for leads
                    </label>
                  </>
                )}
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={editing.enabled !== false}
                         onChange={e => setEditing({ ...editing, enabled: e.target.checked })} />
                  Enabled
                </label>
              </div>
              <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                {shape(editing.providerType).customApi
                  ? 'A Custom API never replaces this business’s CRM — it runs beside it, only for the agents bound to it.'
                  : 'A business can connect two systems — one for booking, one for leads. That is the usual shape in a clinic.'}
              </p>
            </div>

            {formErrors.length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2
                              dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <div className="flex items-center gap-2 font-medium">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {formErrors.length > 1 ? 'Not saved — the server refused:' : 'Not saved'}
                </div>
                <ul className={`${formErrors.length > 1 ? 'list-disc ml-6' : 'ml-6'} mt-1 space-y-0.5 text-[12.5px]`}>
                  {formErrors.map(m => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button className="tl-btn tl-btn-neutral" onClick={() => setEditing(null)}>Cancel</button>
              <button className="tl-btn tl-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmIntegrations;
